import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
    FaMapMarkerAlt,
    FaCreditCard,
    FaClipboardCheck,
    FaArrowLeft,
    FaArrowRight,
    FaShieldAlt,
    FaTruck,
    FaUndo,
    FaCheck,
    FaMoneyBillWave,
    FaUniversity,
    FaWallet,
} from "react-icons/fa";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import "./chckout.css";

/* ── Steps Config ── */
const steps = [
    { label: "Shipping", icon: <FaMapMarkerAlt /> },
    { label: "Payment", icon: <FaCreditCard /> },
    { label: "Review", icon: <FaClipboardCheck /> },
];
const REACT_APP_API_URL = process.env.REACT_APP_API_URL;
/* ── Animation Variants ── */
const pageVariants = {
    hidden: { opacity: 0, y: 40 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut",
            when: "beforeChildren",
            staggerChildren: 0.08,
        },
    },
};

const fadeUp = {
    hidden: { opacity: 0, y: 25 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 120 : -120, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -120 : 120, opacity: 0 }),
};

/* ── Checkout Component ── */
const Checkout = () => {
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [cartItems, setCartItems] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [direction, setDirection] = useState(1);
    const [paymentMethod, setPaymentMethod] = useState("card");
    const { pathname } = useLocation();
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const navigate = useNavigate();

    const [newAddress, setNewAddress] = useState({
        label: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
    });

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        cardNumber: "",
        cardName: "",
        expiry: "",
        cvv: "",
    });

    /* ── Load Saved Addresses ── */
    useEffect(() => {
        const saved = localStorage.getItem("checkoutAddresses");
        if (saved) setAddresses(JSON.parse(saved));
    }, []);

    /* ── Load Cart Items ── */
    useEffect(() => {
        const loadCart = async () => {
            try {
                const token = localStorage.getItem("token");
                const guestId = localStorage.getItem("guestId");

                const res = await fetch(`${REACT_APP_API_URL}/cart?guestId=${guestId}`, {
                    headers: {
                        ...(token && { Authorization: `Bearer ${token}` }),
                    },
                });

                const data = await res.json();

                // Merge duplicate products
                const map = new Map();
                (data.items || []).forEach((i) => {
                    const key = `${i.productId}_${i.color || ""}_${i.memory || ""}_${i.storage || ""}`;
                    if (map.has(key)) {
                        map.get(key).qty += Number(i.quantity);
                    } else {
                        map.set(key, {
                            id: key,
                            productId: i.productId,
                            name: i.name,
                            price: Number(i.price),
                            qty: Number(i.quantity),
                            image: i.image,
                            color: i.color || "",
                            memory: i.memory || "",
                            storage: i.storage || "",
                            variant: [i.color, i.memory, i.storage].filter(Boolean).join(" · "),
                        });
                    }
                });

                setCartItems(Array.from(map.values()));
            } catch (err) {
                console.error("Checkout cart load error", err);
            }
        };
        loadCart();
    }, []);

    /* ── Scroll to top on route change ── */
    useEffect(() => window.scrollTo({ top: 0, behavior: "instant" }), [pathname]);

    const handleInput = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const goNext = () => {
        if (currentStep < 2) {
            setDirection(1);
            setCurrentStep((s) => s + 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const goBack = () => {
        if (currentStep > 0) {
            setDirection(-1);
            setCurrentStep((s) => s - 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    /* ── Calculations ── */
    const subtotal = cartItems.reduce((s, i) => s + i.price * i.qty, 0);
    const shipping = 0;
    const discount = 0;
    const total = subtotal - discount + shipping;
    const fmt = (n) => "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });

    /* ── Place Order ── */
    const handlePlaceOrder = async () => {
        if (!selectedAddress) return alert("Please select a shipping address");

        try {
            setIsPlacingOrder(true);

            const token = localStorage.getItem("token");
            const guestId = localStorage.getItem("guestId");

            const orderPayload = {
                guestId,
                items: cartItems.map(item => ({
                    productId: item.productId,
                    name: item.name,
                    price: item.price,
                    quantity: item.qty,
                    color: item.color,
                    memory: item.memory,
                    storage: item.storage
                })),
                subtotal,
                shipping,
                discount,
                total,
                shippingAddress: {
                    ...selectedAddress,
                    name: `${form.firstName} ${form.lastName}`,
                    email: form.email,
                    phone: form.phone,
                },
                paymentMethod,
            };

            const res = await fetch(`${REACT_APP_API_URL}/orders/place`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
                body: JSON.stringify(orderPayload),
            });

            const data = await res.json(); // ✅ FIX

            if (!res.ok) {
                throw new Error(data.message || "Order failed");
            }

            // ✅ Save for refresh fallback
            localStorage.setItem("lastOrder", JSON.stringify({
                orderNumber: data.orderNumber,
                total
            }));

            // ✅ Navigate with state
            navigate("/order-success", {
                state: {
                    orderNumber: data.orderNumber,
                    total: total
                }
            });

        } catch (err) {
            console.error("Order error:", err);
            alert("❌ Failed to place order");
        } finally {
            setIsPlacingOrder(false);
        }
    };


    /* ── Step Content (plain JSX, NOT inner components) ── */
    /* ── Step Content (Updated Shipping Step) ── */
    const shippingStepContent = (
        <motion.div
            key="shipping"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4 }}
        >
            {/* Shipping Addresses Section */}
            <div className="checkout-section">
                <div className="section-header">
                    <div className="section-icon address"><FaMapMarkerAlt /></div>
                    <h5 className="section-title">Shipping Address</h5>
                </div>

                {addresses.map((addr, idx) => (
                    <motion.div
                        key={idx}
                        className={`saved-address ${selectedAddress === addr ? "selected" : ""}`}
                    >
                        <div
                            className="saved-address-main"
                            onClick={() => setSelectedAddress(addr)}
                            style={{ flex: 1 }}
                        >
                            <div className="saved-address-name">{addr.label}</div>
                            <p className="saved-address-text">
                                {addr.address}, {addr.city} {addr.pincode}, {addr.state}
                            </p>
                        </div>

                        {/* Minimal Delete Icon */}
                        <span
                            className="delete-address-minimal"
                            onClick={(e) => {
                                e.stopPropagation(); // prevent selecting the address
                                const updated = addresses.filter((a) => a !== addr);
                                setAddresses(updated);
                                localStorage.setItem("checkoutAddresses", JSON.stringify(updated));
                                if (selectedAddress === addr) setSelectedAddress(null);
                            }}
                        >
                            ×
                        </span>
                    </motion.div>
                ))}



                <motion.p
                    style={{ color: "#667eea", cursor: "pointer", fontWeight: 600, marginTop: "10px" }}
                    whileHover={{ x: 5 }}
                    onClick={() => setShowAddressForm(!showAddressForm)}
                >
                    + Add a new address
                </motion.p>
            </div>

            {/* Add Address Form Modal-style */}
            {showAddressForm && (
                <div className="checkout-section">
                    <h6 className="mb-3" style={{ fontWeight: 700 }}>Add New Address</h6>
                    <div className="row g-3">
                        <div className="col-12">
                            <input
                                className="checkout-input"
                                placeholder="Label (Home / Office)"
                                value={newAddress.label}
                                onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                            />
                        </div>
                        <div className="col-12">
                            <input
                                className="checkout-input"
                                placeholder="Full Address"
                                value={newAddress.address}
                                onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                            />
                        </div>
                        <Col md={4}><input className="checkout-input" placeholder="City" value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })} /></Col>
                        <Col md={4}><input className="checkout-input" placeholder="State" value={newAddress.state} onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })} /></Col>
                        <Col md={4}><input className="checkout-input" placeholder="Pincode" value={newAddress.pincode} onChange={(e) => setNewAddress({ ...newAddress, pincode: e.target.value })} /></Col>
                    </div>
                    <motion.button
                        className="next-btn mt-3"
                        onClick={() => {
                            if (!newAddress.address) return;
                            const updated = [...addresses, newAddress];
                            setAddresses(updated);
                            localStorage.setItem("checkoutAddresses", JSON.stringify(updated));
                            setSelectedAddress(newAddress);
                            setNewAddress({ label: "", address: "", city: "", state: "", pincode: "" });
                            setShowAddressForm(false);
                        }}
                    >
                        Save Address
                    </motion.button>
                </div>
            )}

            {/* ── Updated Contact Info (Matches Image) ── */}
            <div className="checkout-section">
                <div className="section-header">
                    <div className="section-icon address">
                        <FaMapMarkerAlt />
                    </div>
                    <h3 className="section-title">Contact Information</h3>
                </div>

                <div className="row g-4">
                    <div className="col-lg-6">
                        <label className="input-label">First Name</label>
                        <input
                            type="text"
                            name="firstName"
                            className="checkout-input"
                            placeholder="First Name"
                            value={form.firstName}
                            onChange={handleInput}
                        />
                    </div>

                    <div className="col-lg-6">
                        <label className="input-label">Last Name</label>
                        <input
                            type="text"
                            name="lastName"
                            className="checkout-input"
                            placeholder="Last Name"
                            value={form.lastName}
                            onChange={handleInput}
                        />
                    </div>

                    <div className="col-lg-6">
                        <label className="input-label">Email</label>
                        <input
                            type="email"
                            name="email"
                            className="checkout-input"
                            placeholder="Email"
                            value={form.email}
                            onChange={handleInput}
                        />
                    </div>

                    <div className="col-lg-6">
                        <label className="input-label">Phone</label>
                        <input
                            type="text"
                            name="phone"
                            className="checkout-input"
                            placeholder="Phone"
                            value={form.phone}
                            onChange={handleInput}
                        />
                    </div>
                </div>
            </div>
        </motion.div>
    );

    const paymentStepContent = (
        <motion.div
            key="payment"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4 }}
        >
            <div className="checkout-section">
                <div className="section-header">
                    <div className="section-icon payment"><FaCreditCard /></div>
                    <h5 className="section-title">Payment Method</h5>
                </div>

                <div className="payment-options">
                    {[
                        { key: "card", icon: <FaCreditCard />, label: "Credit / Debit Card", desc: "Visa, Mastercard, Rupay" },
                        { key: "upi", icon: <FaWallet />, label: "UPI Payment", desc: "Google Pay, PhonePe, Paytm" },
                        { key: "netbanking", icon: <FaUniversity />, label: "Net Banking", desc: "All major banks supported" },
                        { key: "cod", icon: <FaMoneyBillWave />, label: "Cash on Delivery", desc: "Pay when you receive" },
                    ].map(opt => (
                        <motion.div key={opt.key} className={`payment-option ${paymentMethod === opt.key ? "selected" : ""}`}
                            onClick={() => setPaymentMethod(opt.key)}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                        >
                            <div className="payment-radio"><div className="payment-radio-inner" /></div>
                            <span className="payment-icon">{opt.icon}</span>
                            <div>
                                <div className="payment-label">{opt.label}</div>
                                <p className="payment-desc">{opt.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <AnimatePresence>
                    {paymentMethod === "card" && (
                        <motion.div className="card-details-grid" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.35 }}>
                            <div className="full-span"><label className="input-label">Card Number</label><input className="checkout-input" name="cardNumber" placeholder="1234 5678 9012 3456" value={form.cardNumber} onChange={handleInput} /></div>
                            <div className="full-span"><label className="input-label">Name on Card</label><input className="checkout-input" name="cardName" placeholder="John Doe" value={form.cardName} onChange={handleInput} /></div>
                            <div><label className="input-label">Expiry Date</label><input className="checkout-input" name="expiry" placeholder="MM/YY" value={form.expiry} onChange={handleInput} /></div>
                            <div><label className="input-label">CVV</label><input className="checkout-input" name="cvv" type="password" placeholder="•••" value={form.cvv} onChange={handleInput} /></div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );

    const reviewStepContent = (
        <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.4 }}>
            {/* Items */}
            <div className="checkout-section">
                <div className="section-header"><div className="section-icon review"><FaClipboardCheck /></div><h5 className="section-title">Order Review</h5></div>
                {cartItems.map(item => (
                    <motion.div className="review-item" key={item.id} variants={fadeUp}>
                        <img src={item.image} alt={item.name} className="review-item-img" />
                        <div><div className="review-item-name">{item.name}</div><div className="review-item-variant">{item.variant}</div></div>
                        <span className="review-item-qty">× {item.qty}</span>
                        <span className="review-item-price">{fmt(item.price * item.qty)}</span>
                    </motion.div>
                ))}
            </div>

            {/* Shipping + Payment Summary */}
            <div className="checkout-section">
                <Row>
                    <Col md={6}>
                        <h6 style={{ fontWeight: 700, marginBottom: 10 }}><FaMapMarkerAlt style={{ color: "#667eea", marginRight: 8 }} />Shipping To</h6>
                        <p style={{ color: "#64748b", fontSize: "0.9rem", marginBottom: 0 }}>
                            {selectedAddress ? `${selectedAddress.address}, ${selectedAddress.city} ${selectedAddress.pincode}, ${selectedAddress.state}` : "No address selected"}
                        </p>
                    </Col>
                    <Col md={6} className="mt-3 mt-md-0">
                        <h6 style={{ fontWeight: 700, marginBottom: 10 }}><FaCreditCard style={{ color: "#f59e0b", marginRight: 8 }} />Payment</h6>
                        <p style={{ color: "#64748b", fontSize: "0.9rem", marginBottom: 0 }}>
                            {paymentMethod === "card" && "Credit / Debit Card"}
                            {paymentMethod === "upi" && "UPI Payment"}
                            {paymentMethod === "netbanking" && "Net Banking"}
                            {paymentMethod === "cod" && "Cash on Delivery"}
                        </p>
                    </Col>
                </Row>

                {/* Place Order */}
                <motion.button
                    className="place-order-btn"
                    onClick={handlePlaceOrder}
                    disabled={isPlacingOrder}
                >
                    {isPlacingOrder ? "⏳ Placing Order..." : `Place Order — ${fmt(total)}`}
                </motion.button>

                <div className="checkout-trust">
                    <span><FaShieldAlt /> Secure Checkout</span>
                    <span><FaTruck /> Free Shipping</span>
                    <span><FaUndo /> Easy Returns</span>
                </div>
            </div>
        </motion.div>
    );

    const stepComponents = [shippingStepContent, paymentStepContent, reviewStepContent];

    return (
        <div>
            <Header />
            <motion.div className="checkout-page" variants={pageVariants} initial="hidden" animate="show">
                <Container>
                    {/* Stepper */}
                    <motion.div className="checkout-stepper" variants={fadeUp}>
                        {steps.map((step, i) => (
                            <React.Fragment key={i}>
                                <motion.div className={`step ${i === currentStep ? "active" : i < currentStep ? "completed" : ""}`}
                                    onClick={() => { if (i < currentStep) { setDirection(-1); setCurrentStep(i); } }}
                                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
                                >
                                    <motion.span className="step-num" animate={i === currentStep ? { scale: [1, 1.15, 1] } : {}} transition={{ duration: 0.5 }}>
                                        {i < currentStep ? <FaCheck /> : i + 1}
                                    </motion.span>
                                    <span className="step-label">{step.label}</span>
                                </motion.div>
                                {i < steps.length - 1 && <div className={`step-line ${i < currentStep ? "done" : ""}`} />}
                            </React.Fragment>
                        ))}
                    </motion.div>

                    <Row>
                        {/* LEFT: Step Content */}
                        <Col lg={8}>
                            <AnimatePresence mode="wait" custom={direction}>
                                {stepComponents[currentStep]}
                            </AnimatePresence>

                            {/* Navigation Buttons */}
                            {currentStep < 2 && (
                                <motion.div className="checkout-nav" variants={fadeUp}>
                                    {currentStep === 0 ? (
                                        <Link to="/cart" className="back-btn" style={{ textDecoration: "none" }}><FaArrowLeft /> Back to Cart</Link>
                                    ) : (
                                        <motion.button className="back-btn" onClick={goBack} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}><FaArrowLeft /> Back</motion.button>
                                    )}
                                    <motion.button className="next-btn" onClick={goNext} disabled={currentStep === 0 && !selectedAddress} whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
                                        {currentStep === 1 ? "Review Order" : "Continue to Payment"} <FaArrowRight />
                                    </motion.button>
                                </motion.div>
                            )}

                            {currentStep === 2 && (
                                <motion.div className="checkout-nav" variants={fadeUp}>
                                    <motion.button className="back-btn" onClick={goBack} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}><FaArrowLeft /> Back to Payment</motion.button>
                                </motion.div>
                            )}
                        </Col>

                        {/* RIGHT: Order Summary */}
                        <Col lg={4}>
                            <motion.div className="checkout-summary" variants={fadeUp}>
                                <h5 className="checkout-summary-title">Order Summary</h5>
                                {cartItems.map((item) => (
                                    <div key={item.id} className="d-flex align-items-center gap-2 mb-2">
                                        <img src={item.image} alt={item.name} style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 8, background: "#f1f5f9", padding: 4 }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: "0.82rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
                                            <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>× {item.qty}</div>
                                        </div>
                                        <span style={{ fontSize: "0.85rem", fontWeight: 700, whiteSpace: "nowrap" }}>{fmt(item.price * item.qty)}</span>
                                    </div>
                                ))}
                                <hr className="summary-divider" />
                                <div className="summary-row"><span className="label">Subtotal</span><span className="value">{fmt(subtotal)}</span></div>
                                <div className="summary-row"><span className="label">Discount</span><span className="value discount">− {fmt(discount)}</span></div>
                                <div className="summary-row"><span className="label">Shipping</span><span className="value shipping-free">FREE</span></div>
                                <hr className="summary-divider" />
                                <div className="summary-total"><span>Total</span><span>{fmt(total)}</span></div>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </motion.div>
            <Footer />
        </div>
    );
};

export default Checkout;